import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import elementResizeDetectorMaker from 'element-resize-detector';
import { first } from 'lodash';
import memoizeOne from 'memoize-one';
import moment from 'moment';

import feSetBookingStartTime from 'actions/booking/feSetBookingStartTime.action';
import feChangeSchedulerPlaceholder from 'actions/scheduler/feChangeSchedulerPlaceholder.action';
import { feCleanCheckoutData } from 'actions/ui.actions';
import navigateToCheckout from 'bl/checkout/navigateToCheckout.bl';
import calculateRangeOfWorkingHours from 'bl/scheduler/calculateRangeOfWorkingHours.bl';
import determineBookingTime from 'bl/scheduler/determineBookingTime.bl';
import determineHiddenStylistIds from 'bl/scheduler/determineHiddenStylistIds.bl';
import fromHoursRangeToExpanded from 'bl/scheduler/fromHoursRangeToExpanded.bl';
import DndContext from 'components/dnd/DndContext.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import FLAG from 'config/flag.singleton';
import DELAY from 'constants/delay.const';
import DND from 'constants/dnd.const';
import ELID from 'constants/elid.const';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import { selectCurrentSalonHideDeletedStylistColumn } from 'selectors/currentSalon.selectors';
import { selectUserStylistId } from 'selectors/simple';
import asArray from 'utils/arrays/asArray.util';
import { firstOf, lastOf } from 'utils/arrays/elements.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromObjectToDataAttributes from 'utils/converters/object/fromObjectToDataAttributes.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import delayed from 'utils/functions/delayed.util';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseJson from 'utils/parsers/parseJson';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import { overridesForStylist } from 'booking/overrides';
import { fromProcedureToAddedService } from 'booking/utils';
import { reorderStylistsThenSave } from 'scheduler/actions';
import SmsButtonNew from 'scheduler/components/common/SmsButtonNew';
import ConfirmationDialog from 'scheduler/components/ConfirmationDialog';
import DayColumn from 'scheduler/components/DayColumn.component';
import DeletedStylistColumn from 'scheduler/components/DeletedStylistColumn';
import TimesColumn from 'scheduler/components/TimesColumn.component';
import { rangeHoursNoChange, resizeProcedureDuration } from 'scheduler/utils';

import ColumnOrderSuccessModal from './ColumnOrderSuccessModal.component';

import 'styles/calendar/index.scss';
import CN from './DayScheduler.module.scss';

const L = logger('DayScheduler');

const HALF_COLUMN_WIDTH_IN_PIXELS = 105;

// noinspection JSCheckFunctionSignatures
const ERD = elementResizeDetectorMaker({ strategy: 'scroll' }); // scroll strategy for ultra performance

const determineDragStylistStatus = withOptions(({ idDrag, stylists }) => {
  if (!idDrag.procedure || !idDrag.procedure.stylist) return void 1;

  const stylistId = idDrag.procedure.stylist.id;
  const filteredStylists = asArray(stylists).filter((s) => s.id === stylistId);

  return isFullArray(filteredStylists) ? first(filteredStylists).status : void 1;
});

class DayScheduler extends React.Component {
  constructor(props) {
    super(props);
    this.scrollRef = React.createRef();
    this.currentTimeRef = React.createRef();

    this.state = {
      wWidth: 0,
      dragEnd: false,
      width: 0,
      rangeByHours: calculateRangeOfWorkingHours({
        openingHours: this.props.openingHours,
        currentDate: this.props.selectedDate,
        step: 15,
        procedures: this.props.procedures,
      }),
      currentActiveProcedure: 0,
      currentModalAction:
        props.location.state && props.location.state.showModalAction ? props.location.state.showModalAction : null,
      lastRanModalAction: null,
      hoveredCell: null,
      procedureId: -1,
      dragStartCell: null,
      timeoutHandle: null,
      showSms: false,
      modalOrderColumns: false,
    };
    this.columnRefs = {};
    this.columnDivRefs = {};
    props.stylists.map((stylist) => {
      this.columnRefs[stylist.id] = React.createRef();
      this.columnDivRefs[stylist.id] = React.createRef();
      return null;
    });
  }

  static getDerivedStateFromProps(nextProps, state) {
    const newRange = calculateRangeOfWorkingHours({
      openingHours: nextProps.openingHours,
      currentDate: nextProps.selectedDate,
      step: 15,
      procedures: nextProps.procedures,
    });
    const changedState = {};
    if (!rangeHoursNoChange(state.rangeByHours, newRange)) {
      changedState.rangeByHours = newRange;
    }

    if (nextProps.location.state && nextProps.location.state.showModalAction) {
      changedState.currentModalAction = nextProps.location.state.showModalAction;
    }
    return changedState;
  }

  componentDidMount() {
    const { stylists, showAllStylist, userStylistId } = this.props;

    // NOTE: this should probably be used in componentWillReceiveProps()
    this.scrollToStylistColumn({ stylistId: userStylistId });
    this.updateWindowDimensions();

    window.addEventListener('resize', this.updateWindowDimensions);

    showAllStylist(stylists.filter((stylist) => stylist?.status !== 'deleted'));

    this.schedulerScrollEl = window.document.getElementById(ELID.schedscroll);

    const offsetTop = this.currentTimeRef?.current?.offsetTop;
    if (offsetTop) {
      // -96 because 1 cell is 24px height and 4x24 is 96px
      this.schedulerScrollEl.scrollTop = parseFloat(offsetTop) - 96;
      if (this.scrollRef?.current?.clientWidth) ERD.listenTo(this.schedulerScrollEl, this.updateWidth);
    }

    this.tryToFireModalAction();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.messageSent) {
      setTimeout(() => void this.showSmsPopup(false), 2000);
    }

    if (snapshot) {
      if (this.props.booking.currentStylistId) {
        const selectedStylistRef = this.columnDivRefs[this.props.booking.currentStylistId];
        if (selectedStylistRef && selectedStylistRef.current && selectedStylistRef.current.offsetLeft) {
          this.schedulerScrollEl.scrollLeft = snapshot;
        }
      }
    }

    this.props.stylists.map((stylist) => {
      if (!this.columnRefs[stylist.id]) {
        this.columnRefs[stylist.id] = React.createRef();
        this.columnDivRefs[stylist.id] = React.createRef();
      }
      return null;
    });

    this.tryToFireModalAction();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    ERD.removeListener(this.schedulerScrollEl);
  }

  getSnapshotBeforeUpdate = (prevProps, prevState) => {
    if (
      prevProps.booking.currentStylistId !== this.props.booking.currentStylistId ||
      (prevState.width !== this.state.width && this.props.booking.currentStylistId)
    ) {
      // We've put a placeholder in, so let's fix the scroll location
      if (this.selectedStylistRef && this.selectedStylistRef.current && this.selectedStylistRef.current.offsetLeft) {
        return this.selectedStylistRef.current.offsetLeft;
      }
    }
    return null;
  };

  setClassIfManyColumns = () => {
    const stylistList =
      this.props.show_only_active && this.props.displayStylists.length !== 0
        ? this.props.stylists.filter((s) => this.props.displayStylists.some((d) => d.id === s.id))
        : this.props.stylists.filter((stylist) => stylist.status === 'active');

    const colWidth = this.state.wWidth / stylistList.length;

    return colWidth < 295 ? 'narrow-column' : 'normal-column';
  };

  notifyClient = () => void this.setState({ showSms: true });

  showSmsPopup = (tf) => void this.setState({ showSms: tf });

  /**
   * This function will try to show appoinment/ procedure's modal, most probably clicked from the client profile
   * The idea is that a parameter is passed via the router ( from the client profile) and this parameter is the
   * "action". The first thing that is done is to check if the appointment's date is the current calendar date,
   * if it is not change the date and once it is updated it will again come to this place but this time with
   * the right date, once that is done it will try to find the appointment by id. The id is set in the
   * SchedulerProcedure.jsx as `appointment_${appointment.id}` and if it finds an element like that it will
   * trigger the click event on it and that automatically will trigger all other logic about showing the modal.
   *
   * - If it doesn't find an element with the id passed the action will stay there until it finds the element
   * (on component update) OR the action changes
   * - If it finds the element and everything is fine the "lastRanModalAction" prop of the state is set so the
   * action is not done twice.
   */
  tryToFireModalAction = () => {
    if (
      this.state.currentModalAction &&
      (!this.state.lastRanModalAction ||
        this.state.lastRanModalAction.definedTimestamp !== this.state.currentModalAction.definedTimestamp)
    ) {
      const el = document.getElementById(`appointment_${this.state.currentModalAction.appointmentId}`);
      if (
        this.props.selectedDate.format('YYYY-MM-DD') !==
        moment(this.state.currentModalAction.dateToSwitchTo).format('YYYY-MM-DD')
      ) {
        this.props.dayPick(moment(this.state.currentModalAction.dateToSwitchTo));
        return;
      }
      if (el) {
        setTimeout(() => {
          el.click();
          this.setState({ lastRanModalAction: this.state.currentModalAction });
          if (this.state.currentModalAction && this.state.currentModalAction.openSmS) {
            const cardModalEl = document.querySelector('.sms-compose-button');
            if (cardModalEl) {
              setTimeout(() => {
                cardModalEl.click();
              }, 1000);
            }
          }
        }, 1000);
      }
    }
  };

  // eslint-disable-next-line react/sort-comp
  filterProcedures = memoizeOne((list, stylist, hideNoShow, dayStartTime, dayEndTime) =>
    list.filter((procedure) => {
      const { hideNoShow: isHideNoShow } = this.props;

      const stylistId = stylist?.id;

      const status = procedure?.appointment?.status;
      const bgnMoment = moment(procedure?.start_time);
      const isHidden = (isHideNoShow && AP_STATUS.canceled === status) || (isHideNoShow && AP_STATUS.noShow === status);

      return (
        !isHidden &&
        procedure?.stylist &&
        procedure?.stylist?.id === stylistId &&
        bgnMoment.isAfter(dayStartTime) &&
        bgnMoment.isBefore(dayEndTime)
      );
    }),
  );

  filterEvents = memoizeOne((list, stylist) => list.filter((event) => event?.stylist_id === stylist?.id));

  updateWidth = () => {
    if (this.scrollRef && this.scrollRef.current && this.scrollRef.current.clientWidth) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ width: this.scrollRef.current.clientWidth });
    }
  };

  markProcedures = (appointmentId, procedureId, mark) => {
    this.setState({ currentActiveProcedure: procedureId });
    this.props.markAppointmentEdit(appointmentId, mark);
  };

  undoProcedure = () => {
    this.props.undoProcedure(this.props.salonId, this.state.procedureId);
  };

  checkForColumnNumber = () => {
    const stylistList =
      this.props.show_only_active && this.props.displayStylists.length !== 0
        ? this.props.stylists.filter((s) => this.props.displayStylists.some((d) => d.id === s.id))
        : this.props.stylists.filter((stylist) => stylist.status === 'active');

    if (stylistList.length === 2) {
      return 'two-stylist';
    }
    if (stylistList.length === 1) {
      return 'one-stylist';
    }
    return 'three-and-more-stylists';
  };

  updateWindowDimensions = () => void this.setState({ wWidth: window.innerWidth });

  scrollToStylistColumn = ({ stylistId }) => {
    if (!stylistId) {
      // in some cases the ID is missing e.g. customer support, wait list etc.
      return;
    }

    // it is set by ScheduleColumn
    // SEE: src/scheduler/components/ScheduleColumn.jsx
    const stylistSelector = `[data-stylist-id="${stylistId}"]`;

    delayed(DELAY.attention, () => {
      const halfWindowWidth = window.innerWidth / 2 - HALF_COLUMN_WIDTH_IN_PIXELS;
      const focusedColumn = document.querySelector(stylistSelector);

      if (!focusedColumn) return;

      const elementPosition = focusedColumn.getBoundingClientRect();
      window.scrollTo(elementPosition.left - halfWindowWidth, 0);
    });
  };

  onCheckout = (appointment) =>
    void navigateToCheckout({
      appointment,
      history: this.props.history,
      isDay: true,
      onClean: this.props.feCleanCheckoutData,
      onData: this.props.setCheckoutInitData,
      pdi: this.props.procedures_date_index,
      procedures: this.props.procedures,
      salonId: this.props.salonId,
      tag: 'DayScheduler.onCheckout()',
    });

  onResizeProcedure = (procedure) => {
    if (procedure.editing && procedure.appointment) {
      this.props.updateService(fromProcedureToAddedService(procedure));
    } else {
      this.props.updateProcedure(this.props.salonId, procedure);
    }
  };

  onMoveProcedure = withOptions(({ time: timeFromOptions, stylist, procedure }) => {
    const time = moment(timeFromOptions);
    L.debug('onMoveProcedure()', timeFromOptions, { time, stylist, procedure });

    const {
      preferenceList,
      updateService,
      updateProcedure,
      showModal,
      salonId,
      stylists,
      categoriesAndServices: categoryServices,
    } = this.props;

    const {
      appointment,
      duration: procedureDuration,
      editing,
      id: procedureId,
      is_custom_processing: isCustomProcessing,
      is_custom_duration: isCustomDuration,
      is_custom_price: isCustomPrice,
      local_id: procedureLocalId,
      price: procedurePrice,
      processing: procedureProcessing,
      service,
      stylist: procedureStylist,
    } = asObject(procedure);

    const { id: procedureStylistId } = asObject(procedureStylist);
    const { category: serviceCategory, id: serviceId, name: serviceName } = asObject(service);
    const { color: serviceCategoryColor, id: serviceCategoryId } = asObject(serviceCategory);

    const startTime = time.toISOString();
    const stylistId = asObject(stylist).id;

    const serviceStyle = {
      id: procedureId,
      service: {
        id: serviceId,
        name: serviceName,
        category: serviceCategory,
        duration: procedureDuration,
        processing: procedureProcessing,
        price: procedurePrice,
        variable_pricing: false,
        role_executions: [],
      },
      service_id: serviceId,
      category: serviceCategoryId,
      color: serviceCategoryColor,
      name: serviceName,
      stylist_id: stylistId,
      stylist,
      editing,
      price: procedurePrice,
      variable_pricing: false,
      duration: procedureDuration,
      processing: procedureProcessing,
      start_time: startTime,
      end_time: moment(time).add(procedureDuration),
      role_executions: [],
      deleted: false,
      is_custom_price: isCustomPrice,
      is_custom_duration: isCustomDuration,
      is_custom_processing: isCustomProcessing,
    };

    if (editing && appointment) {
      // for editing and appointment use-case, this is it, update and exit
      // there is a type incompatibility here between the two appointments
      updateService({ ...serviceStyle, appointment, local_id: procedureLocalId });
      return;
    }

    // here continues the more complex move, one that creates new procedure

    const newProcedure = { ...procedure };
    if (newProcedure.id < 0) delete newProcedure.id;

    // if the stylist is the same, newProcedure already has the values
    // otherwise it's a new stylist and the values might need to be overridden

    // if the stylist doesn't change, newProcedure has the values
    // otherwise, use the determined overrides for the new stylist
    const { price, duration, processing } =
      stylistId === procedureStylistId
        ? newProcedure
        : overridesForStylist({
            categoryServices,
            service: serviceStyle,
            stylistId,
            stylists,
          });

    Object.assign(newProcedure, { price, duration, processing, stylist, start_time: startTime });

    if (appointment && appointment.status === 'confirmed') {
      showModal(
        ({ onRequestClose }) => (
          <ConfirmationDialog
            onRequestClose={onRequestClose}
            title="Warning"
            leftButtonText="NO, DON'T MOVE"
            onLeft={onRequestClose}
            rightButtonText="YES, MOVE IT"
            onRight={() => {
              updateProcedure(salonId, newProcedure);
              onRequestClose();
            }}
            onCancel={onRequestClose}
          >
            <p>The appointment time has already been confirmed by the client Are you sure you want to move this?</p>
          </ConfirmationDialog>
        ),
        {},
      );
      return;
    }

    if (stylistId === procedureStylistId) {
      updateProcedure(salonId, newProcedure);
      return;
    }

    const procedureServiceId = newProcedure.service.id;
    const preference = preferenceList.find(({ service }) => service === procedureServiceId);
    if (!preference || preference.stylist === stylistId) {
      updateProcedure(salonId, newProcedure);
      return;
    }

    showModal(
      ({ onRequestClose }) => (
        <ConfirmationDialog
          onRequestClose={onRequestClose}
          leftButtonText="NO, GO BACK"
          onLeft={onRequestClose}
          rightButtonText="YES, CONTINUE"
          onRight={() => {
            updateProcedure(salonId, newProcedure);
            onRequestClose();
          }}
          onCancel={onRequestClose}
        >
          <div className={CN.modalContent}>
            <IconStatus variant="alert">
              <Icon variant="alert" />
            </IconStatus>
            <div className={CN.modalTitle}>You have selected a different stylist</div>
            <span className={CN.modalText}>
              The client has selected a different preferred stylist for this service. Continue with your chosen stylist?
            </span>
          </div>
        </ConfirmationDialog>
      ),
      {},
    );
  });

  onMoveEvent = (time, stylistId, event, extend) => {
    L.debug('onMoveEvent()', { time, stylistId, event, extend });

    let newEvent;

    if (extend) {
      let endTime = time.toISOString();
      if (!moment(time).isAfter(moment(event.start_time))) {
        endTime = moment(event.start_time).add(15, 'minutes');
      }
      newEvent = {
        ...event,
        end_time: endTime,
      };
    } else {
      newEvent = {
        ...event,
        stylist_id: stylistId,
        start_time: time.toISOString(),
        end_time: moment(time)
          .add(moment(event.end_time).diff(moment(event.start_time)))
          .toISOString(),
      };
    }

    if (newEvent.client === null) {
      delete newEvent.client;
    }
    const Modal = ({ onRequestClose, e, createUpdateTimeoff }) => (
      <ConfirmationDialog
        onRequestClose={onRequestClose}
        title="Repeating entry"
        leftButtonText="All"
        onLeft={() => createUpdateTimeoff({ ...e, update_all: true }, 'PUT', event).then(onRequestClose)}
        rightButtonText="Just today"
        onRight={() => createUpdateTimeoff({ ...e, update_all: false }, 'PUT', event).then(onRequestClose)}
        onCancel={onRequestClose}
      >
        <p>This is a repeating entry.</p>
        <p>You can change ALL of the entries or just make today&apos;s different to the others</p>
      </ConfirmationDialog>
    );
    if (newEvent.recurrences) {
      this.props.showModal(Modal, {
        e: newEvent,
        createUpdateTimeoff: this.props.createUpdateTimeoff,
      });
    } else {
      this.props.createUpdateTimeoff(newEvent, 'PUT', event);
    }
  };

  onDeleteEvent = (options) => {
    const { event, all } = L.debug('onDeleteEvent()', 'options:', options) ?? {};

    const Modal = ({ onRequestClose, e, createUpdateTimeoff }) => (
      <ConfirmationDialog
        onRequestClose={onRequestClose}
        title="Repeating entry"
        leftButtonText="Delete all"
        onLeft={() => createUpdateTimeoff({ ...e, update_all: true }, 'DELETE').then(onRequestClose)}
        rightButtonText="Delete selected only"
        onRight={() => createUpdateTimeoff({ ...e, update_all: false }, 'DELETE').then(onRequestClose)}
        onCancel={onRequestClose}
      >
        <p>This is a repeating entry.</p>
        <p>You can delete all of the entries or just one</p>
      </ConfirmationDialog>
    );

    if (event.recurrences) {
      this.props.showModal(Modal, { e: event, createUpdateTimeoff: this.props.createUpdateTimeoff });
    } else {
      this.props.createUpdateTimeoff({ ...event, update_all: all }, 'DELETE');
    }
  };

  onDragStart = (ev) => {
    const { setDragFrom } = this.props;
    const { dragStartCell } = this.state;

    if (!dragStartCell) this.setState({ dragEnd: false, dragStartCell: parseJson(ev.source.droppableId) });

    const idDrag = parseJson(ev?.draggableId);
    const stylistId = idDrag?.procedure?.stylist?.id || idDrag?.procedure?.stylist_id;

    if (DND.dragProcedure === idDrag?.type) setDragFrom(stylistId);
  };

  onTouchAtLocation = (touches) => {
    let sumX = 0;
    let sumY = 0;
    for (let i = 0; i < touches.length; i += 1) {
      sumX += touches[i].clientX;
      sumY += touches[i].clientY;
    }
    const aveTouches = { clientX: sumX / touches.length, clientY: sumY / touches.length };
    for (let i = 0; i < this.props.stylists.length; i += 1) {
      const columnRef = this.columnDivRefs[this.props.stylists[i].id];
      if (columnRef && columnRef.current) {
        if (
          aveTouches.clientX > columnRef.current.offsetLeft - this.schedulerScrollEl.scrollLeft &&
          aveTouches.clientX <
            columnRef.current.offsetLeft - this.schedulerScrollEl.scrollLeft + columnRef.current.offsetWidth
        ) {
          let dragEl;

          let dragBounding;
          if (dragEl) {
            dragBounding = dragEl.getBoundingClientRect();

            const cellData = this.columnRefs[this.props.stylists[i].id].current
              .getWrappedInstance()
              .dropLocation(dragBounding.left, dragBounding.top, 0, 0);

            if (
              (this.state.hoveredCell &&
                cellData.time &&
                (this.state.hoveredCell.stylistId !== this.props.stylists[i].id ||
                  cellData.time !== this.state.hoveredCell.time)) ||
              this.state.hoveredCell === null
            ) {
              if (!cellData.calculated) {
                this.setState({ hoveredCell: { stylistId: this.props.stylists[i].id, time: cellData.time } });
              }
              break;
            }
          }
        }
      }
    }
  };

  onTouchMove = (ev) => void this.onTouchAtLocation(ev.targetTouches);

  onTouchEnd = (ev) => {
    if (this.props.procedureDropTime) return;
    this.setState({ dragEnd: true });
    if (!this.state.hoveredCell) this.onTouchAtLocation(ev.changedTouches);
  };

  onDragEnd = (ev) => {
    const { feSetBookingStartTime, trackEvent, setDropTime, setEndTime, stylists, feChangeSchedulerPlaceholder } =
      this.props;
    const { timeoutHandle: timeoutHandleFromState, dragStartCell, rangeByHours } = this.state;

    if (!ev.destination || fromAnyToString(ev?.draggableId).indexOf('placeholder') > 0) return;

    const cellData = {};
    const elProcedureModal = document.querySelector(`#${ELID.bookside}`);

    const idDrag = parseJson(ev?.draggableId);
    const idDropTarget = parseJson(ev?.destination?.droppableId);

    const { event, procedure, type: idDragType } = idDrag ?? {};
    const { type: idDropTargetType, stylist, time: dropTime } = idDropTarget ?? {};

    const dropStylistId = stylist?.id;
    const dropStylistStatus = determineDragStylistStatus({ idDrag, stylists });

    const time = dropStylistStatus === 'deleted' || !dropStylistStatus ? procedure?.start_time : dropTime;

    if (
      stylist &&
      idDropTargetType === DND.dragStylistColumn &&
      (idDragType === DND.dragProcedure || idDragType === DND.dragEvent)
    ) {
      // Drag a procedure or event to the column header, let's drop at the first time in the day
      cellData.time = rangeByHours[0].toISOString();
      if (idDragType === DND.dragProcedure) {
        trackEvent('DaySchedulerProcedureDragTop');
        if (!elProcedureModal) {
          setDropTime(Date.now());
          if (timeoutHandleFromState) {
            clearTimeout(timeoutHandleFromState);
            this.setState({ timeoutHandle: 0 });
          }
          const timeoutHandle = setTimeout(() => void setDropTime(0), 5000);
          this.setState({ timeoutHandle });
        }

        this.onMoveProcedure({ time, stylist, procedure });
      } else if (idDragType === DND.dragEvent) {
        trackEvent('DaySchedulerEventDragTop');
        this.onMoveEvent(moment(cellData.time), dropStylistId, event);
      }
    }

    if (idDragType === DND.dragProcedure && idDropTargetType === DND.dropCell) {
      trackEvent('DaySchedulerProcedureDragEnd');

      if (procedure) {
        const procedureId = procedure.id;
        this.setState({ procedureId });
        const moved = dropStylistId !== dragStartCell.stylist.id || dropTime !== dragStartCell.time;
        if (!elProcedureModal && moved) {
          setDropTime(Date.now());
          // rest handle after each drag
          if (timeoutHandleFromState) {
            clearTimeout(timeoutHandleFromState);
            this.setState({ timeoutHandle: 0 });
          }
          const timeoutHandle = setTimeout(() => void setDropTime(0), 5000);
          this.setState({ timeoutHandle });
        }
        if (moved) {
          this.onMoveProcedure({ time, stylist, procedure });
          this.setState({ dragEnd: true, dragStartCell: null });
        }
      }
    } else if (idDragType === DND.resizeProcedure && idDropTargetType === DND.dropCell) {
      trackEvent('DaySchedulerProcedureResizeDragEnd');
      // endTime is cellData.time
      this.onResizeProcedure(resizeProcedureDuration({ procedure, endTime: dropTime }));
    } else if (idDragType === DND.dragEvent && idDropTargetType === DND.dropCell) {
      trackEvent('DaySchedulerEventDragEnd');

      if (event?.id > 0) {
        // saved event, update the remote value
        this.onMoveEvent(moment(dropTime), dropStylistId, event);
      } else {
        // local only event, update the local store
        feSetBookingStartTime(moment(dropTime));
        setEndTime(moment(dropTime).add(moment(event?.end_time).diff(moment(event?.start_time))));
      }
    } else if (idDragType === DND.resizeEvent && idDropTargetType === DND.dropCell) {
      trackEvent('DaySchedulerEventResizeDragEnd');
      const startTime = dropTime;
      if (event?.id > 0) {
        this.onMoveEvent(moment(startTime), 0, event, true);
      } else {
        setEndTime(moment(startTime));
      }
    } else if (idDragType === DND.dragStylistColumn && stylist && idDrag.stylist.id !== dropStylistId) {
      trackEvent('DaySchedulerStylistColumnDragEnd');
      this.onMoveColumn(idDrag.stylist, stylist);
    } else if (idDragType === DND.dragPlaceholder && idDropTargetType === DND.dropCell) {
      feChangeSchedulerPlaceholder({ stylistId: dropStylistId, time: cellData.time });
    }
  };

  onMoveColumn = (first, next) => {
    this.props.reorderStylistsThenSave(first, next);
    if (next) {
      this.setState({ modalOrderColumns: true });
    }
  };

  render() {
    const {
      currentDay,
      booking,
      selectedDate,
      timezone,
      saveOrderResponse,
      openingHours,
      show_only_active: showOnlyActive,
      displayStylists: displayStylistsFromProps,
      stylists: stylistsFromProps,
      events,
    } = this.props;

    const { rangeByHours, modalOrderColumns } = this.state;

    let movedProcedure;
    let clientId;
    let movedFirstService = true;
    if (this.state.procedureId > 0) {
      [movedProcedure] = this.props.procedures.filter((p) => p.id === this.state.procedureId);
      if (movedProcedure && movedProcedure.appointment.client) {
        const appId = movedProcedure.appointment.id;
        const allProcedures = this.props.procedures;

        for (let i = 0; i < allProcedures.length; i += 1) {
          if (
            allProcedures[i].appointment &&
            allProcedures[i].appointment.id === appId &&
            allProcedures[i].id !== movedProcedure.id
          ) {
            // there is another procedure in this appointment
            if (moment(allProcedures[i].start_time).isBefore(moment(movedProcedure.start_time))) {
              movedFirstService = false;
            }
          }
        }
        clientId = movedProcedure.appointment.client.id;
      }
    }
    let clientPhone;
    if (clientId) {
      const foundClient = this.props.clientList.filter((c) => c.id === clientId);
      if (foundClient.length > 0) {
        clientPhone = foundClient[0].phone;
      }
    }

    const stylistListWithNonWorking =
      showOnlyActive && displayStylistsFromProps.length !== 0
        ? stylistsFromProps.filter((s) => displayStylistsFromProps.some((d) => d.id === s.id))
        : stylistsFromProps.filter((s) => s.status === 'active');

    const hiddenStylistIds = determineHiddenStylistIds({
      currentDay,
      events,
      tz: timezone,
      tag: 'DayScheduler',
    });

    const stylistList = [];
    for (let i = 0; i < stylistListWithNonWorking.length; i += 1) {
      if (hiddenStylistIds.indexOf(stylistListWithNonWorking[i].id) < 0 && stylistListWithNonWorking[i].has_column) {
        stylistList.push(stylistListWithNonWorking[i]);
      }
    }

    const calcOpen = openingHours.filter((d) => d.shortName === selectedDate.format('ddd').toLowerCase());
    const isSalonOpen = calcOpen[0].open;

    const addedServicesIds = booking.addedServices.filter((o) => !o.deleted).map((o) => o.id);
    const deletedServicesIds = booking.addedServices.filter((o) => o.deleted).map((o) => o.id);
    const deletedProcedureIds = this.props.procedures
      .filter((o) => o.appointment && o.appointment.status === 'deleted')
      .map((o) => o.id);

    const deletedStylists = stylistsFromProps.filter((o) => o.status === 'deleted');
    const deletedProcedures = this.props.procedures.filter(
      (o) =>
        o.stylist &&
        deletedStylists.findIndex((stylist) => stylist.id === o.stylist.id) !== -1 &&
        moment(o.start_time).isSameOrAfter(rangeByHours[0]) &&
        moment(o.start_time).isSameOrBefore(rangeByHours.slice(-1).pop()),
    );

    const columnWidth = this.setClassIfManyColumns() || '';
    const expandedRange = FLAG.expandDayOffHours ? fromHoursRangeToExpanded(rangeByHours) : rangeByHours;

    return (
      <div
        data-bem="DayScheduler"
        className={`${this.checkForColumnNumber()} column-in-viewport`}
        ref={this.scrollRef}
        onTouchMove={this.onTouchMove}
        onTouchEnd={this.onTouchEnd}
        role="grid"
        tabIndex={0}
      >
        <DndContext tag="DayScheduler--dnd-context" onDragEnd={this.onDragEnd} onDragStart={this.onDragStart}>
          <div
            className={cn({
              'DAY-VIEW the-scheduler-if-self': true,
              'column-off-container': !isSalonOpen,
            })}
          >
            <TimesColumn
              isDay
              selectedDate={selectedDate}
              range={expandedRange}
              currentTimeRef={this.currentTimeRef}
              tz={timezone}
              extraClass
            />

            {stylistList.map((stylist) => {
              const stylistId = stylist.id;

              const currentAddedServices = booking.addedServices.filter(
                (service) => service.stylist_id === stylistId && !service.deleted,
              );

              const allProceduresForThisStylist = this.filterProcedures(
                this.props.procedures,
                stylist,
                this.props.hideNoShow,
                firstOf(rangeByHours),
                lastOf(rangeByHours),
              );

              const bookingTime = determineBookingTime({ booking, stylistId });

              const newEntryIndicator = fromAnyToBool(
                booking && booking.currentStylistId === stylistId && !addedServicesIds.length,
              );

              const filteredNoNewlyAddedProcedures = allProceduresForThisStylist.filter(
                (procedure) =>
                  !addedServicesIds.includes(procedure.id) &&
                  !deletedServicesIds.includes(procedure.id) &&
                  !deletedProcedureIds.includes(procedure.id),
              );

              return (
                <div
                  data-bem="DayScheduler__column-container"
                  id={`column-${stylistId}`}
                  className={cn({
                    'column-container': true,
                    [columnWidth]: true,
                  })}
                  key={stylistId}
                  ref={this.columnDivRefs[stylistId]}
                  {...fromObjectToDataAttributes({ stylistId, columnWidth })}
                >
                  <DayColumn
                    ref={this.columnRefs[stylistId]}
                    addedServices={this.props.isPosDeposit ? [] : currentAddedServices}
                    allProcedures={allProceduresForThisStylist}
                    bookingTime={bookingTime}
                    categoriesAndServices={this.props.categoriesAndServices}
                    currentActiveProcedure={this.state.currentActiveProcedure}
                    dragEnd={this.state.dragEnd}
                    events={this.filterEvents(this.props.events, stylist)}
                    filteredNoNewlyAddedProcedures={filteredNoNewlyAddedProcedures}
                    history={this.props.history}
                    hovered={this.props.hovered}
                    hoveredCell={this.state.hoveredCell}
                    markAppointmentEdit={this.props.markAppointmentEdit}
                    markProcedures={this.markProcedures}
                    newEntryIndicator={newEntryIndicator}
                    onCheckout={this.onCheckout}
                    onDeleteEvent={this.onDeleteEvent}
                    onMoveColumn={this.onMoveColumn}
                    openingHours={openingHours}
                    pickCurrentStylist={this.props.pickCurrentStylist}
                    rangeByHours={expandedRange}
                    salonId={this.props.salonId}
                    salonTimezone={timezone}
                    selectedDate={selectedDate}
                    setWeekView={this.props.setWeekView}
                    showModal={this.props.showModal}
                    stylist={stylist}
                    stylists={stylistsFromProps}
                    updateProcedure={this.props.updateProcedure}
                    updateProcedureStatus={this.props.updateProcedureStatus}
                    visibilityWhenManyColumns={this.setClassIfManyColumns}
                    handleProfileImageClick={this.props.handleProfileImageClick}
                  />
                </div>
              );
            })}

            {!this.props.isHideDeletedStylistColumn && (
              <div className="column-container">
                <DeletedStylistColumn
                  hovered={this.props.hovered}
                  salonId={this.props.salonId}
                  salonTimezone={timezone}
                  history={this.props.history}
                  procedures={deletedProcedures}
                  rangeByHours={expandedRange}
                  openingHours={openingHours}
                  onCheckout={this.onCheckout}
                  onResizeProcedure={this.onResizeProcedure}
                  pickCurrentStylist={this.props.pickCurrentStylist}
                  updateProcedureStatus={this.props.updateProcedureStatus}
                  updateProcedure={this.props.updateProcedure}
                />
              </div>
            )}
          </div>
        </DndContext>
        {this.props.procedureDropTime && (
          <div className={clientId && clientPhone ? 'move-notification' : 'move-notification two-btn'}>
            <div className="moved-btn" role="button" tabIndex={0} onClick={this.undoProcedure}>
              Appointment moved
            </div>
            {clientId && clientPhone && movedFirstService && (
              <div className="notify-btn" role="button" tabIndex={0} onClick={this.notifyClient}>
                Confirm change
              </div>
            )}

            <div className="undo-btn" role="button" tabIndex={-1} onClick={this.undoProcedure}>
              Undo move
            </div>
          </div>
        )}
        {this.state.showSms && (
          <SmsButtonNew
            procedure={this.props.procedures.filter((p) => p.id === this.state.procedureId)[0]}
            bucketType="appointment"
            smsComposeModalOpen={this.state.showSms}
            showParentSmsPopup={this.showSmsPopup}
            moveNotify
            stylists={stylistsFromProps}
            procedureDragFrom={this.props.procedureDragFrom}
          />
        )}
        {fromAnyToBool(saveOrderResponse && modalOrderColumns) && (
          <ColumnOrderSuccessModal onClick={() => void this.setState({ modalOrderColumns: false })} />
        )}
      </div>
    );
  }
}

const DaySchedulerHoc = connect(
  (state) => ({
    isHideDeletedStylistColumn: selectCurrentSalonHideDeletedStylistColumn(state),
    isPosDeposit: state.scheduler.isPosDeposit,
    userStylistId: selectUserStylistId(state),
    saveOrderResponse: state?.scheduler?.stylistSaveOrder?.response,
    isLoading: state?.scheduler?.stylistSaveOrder?.isFetching,
  }),
  mapActionsToDispatchToProps({
    feChangeSchedulerPlaceholder,
    feCleanCheckoutData,
    feSetBookingStartTime,
    reorderStylistsThenSave,
  }),
)(DayScheduler);

export default DaySchedulerHoc;
